import { useLookbookNavigationSettingsContext } from '@app/components/lookbook/LookbookSettingsPage/contexts';
import { SectionCard } from '@app/components/various/SectionCard';
import { Skeleton } from '@app/components/various/Skeleton';
import { SkeletonLayout } from '@app/components/various/SkeletonLayout';
import { Sortable } from '@app/components/various/Sortable';
import { useLookbookNavigationValidation } from '@app/utils/hooks/lookbook/useLookbookNavigationValidation';
import { useConfirmationGuard } from '@app/utils/hooks/useConfirmationGuard';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Id, Lookbook, Sortable as SortableTypes } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AddedNavigationItem } from '.';

interface Props {
  isLoading?: boolean;
}

export const AddedNavigationItems = ({ isLoading = false }: Props) => {
  const { t } = useTranslation(['common', 'settings', 'confirmationConfig']);
  const { addedMenuItems, addedMenuItemsRef, setNavigationItems, isUnavailablePage, itemRecords, pagesRecords } =
    useLookbookNavigationSettingsContext();
  const confirmationGuard = useConfirmationGuard();
  const { canNest } = useLookbookNavigationValidation({ itemRecords, pagesRecords });

  const shouldShowWarning = isUnavailablePage;

  const addedIds = React.useMemo(
    () =>
      addedMenuItems.map(item => ({
        children: item.children?.map(child => ({ children: [], id: child.id, label: child.label })),
        id: item.id,
        label: item.label,
      })),
    [addedMenuItems],
  );

  const getDetailedItems = React.useCallback(
    (itemsList: SortableTypes.Items<string>): Lookbook.NavigationItem[] => {
      const detailedItems = itemsList.map(item => ({
        ...itemRecords[item.id],
        children: item.children?.map(child => ({ ...itemRecords[child.id] })),
      }));

      return detailedItems as Lookbook.NavigationItem[];
    },
    [itemRecords],
  );

  const handleSortEnd = React.useCallback(
    (itemsOrder: SortableTypes.Items<string>) => {
      setNavigationItems(getDetailedItems(itemsOrder));
    },
    [getDetailedItems, setNavigationItems],
  );

  const getHasChildItems = React.useCallback(
    (id: Id) => {
      const item = itemRecords[id];
      const itemChildren = item?.children;

      return isDefined(itemChildren) && !isEmpty(itemChildren);
    },
    [itemRecords],
  );

  const renderAddedMenuItem = (id: Id) => <AddedNavigationItem key={id} id={id} onRemoveMenuItem={handleRemoveClick} />;

  const removeMenuItem = React.useCallback(
    (id: Id) => {
      const filteredList = addedIds.reduce((acc, item) => {
        if (item.id === id) {
          return acc;
        }

        const newItem = { ...item, children: item.children?.filter(child => child.id !== id) };

        return [...acc, newItem];
      }, []);

      setNavigationItems(getDetailedItems(filteredList));
    },
    [addedIds, setNavigationItems, getDetailedItems],
  );

  const handleRemoveClick = React.useCallback(
    (id: Id) => {
      const hasNested = getHasChildItems(id);

      if (hasNested) {
        confirmationGuard(() => ({
          onOk: () => removeMenuItem(id),
          ...t('confirmationConfig:delete_nested_menu', { returnObjects: true }),
        }))();

        return;
      }

      removeMenuItem(id);
    },
    [getHasChildItems, removeMenuItem, confirmationGuard, t],
  );

  return (
    <div className="flex-1 basis-1/2 overflow-hidden">
      <SectionCard status={shouldShowWarning ? 'warning' : undefined}>
        <div className="mb-5 flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 heading-3">{t('settings:menu_structure')}</h3>
        </div>
        <div className="h-96 overflow-auto" ref={addedMenuItemsRef}>
          <SkeletonLayout isLoading={isLoading} skeleton={<Skeleton type="rectangle" className="h-full" />}>
            <Sortable
              maxDepth={1}
              dataTestId="navigationMenuSortable"
              dragMode="handle"
              handlePosition="start"
              items={addedIds}
              renderItem={renderAddedMenuItem}
              onSortEnd={handleSortEnd}
              validateNesting={canNest}
            />
          </SkeletonLayout>
        </div>
      </SectionCard>
    </div>
  );
};
